import { useHistory, useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import qs from 'qs';

interface useUrlSearchParamsReturnValue<T> {
    urlSearchParams: T;
    setUrlSearchParams: (values: Record<string, any>) => void;
}

const booleanValueMap: { [key: string]: boolean } = {
    true: true,
    True: true,
    false: false,
    False: false
};

function parsePrimitiveValues(object: Record<string, any>) {
    const parsedValues: Record<string, any> = {};

    for (const objectKey in object) {
        const value = object[objectKey];

        let parsedValue;

        if (typeof value === 'object' && !Array.isArray(value)) {
            parsedValue = parsePrimitiveValues(value);
        }
        //@ts-ignore
        else if (parseInt(value, 10) == value && !(value.startsWith('0') && value.length > 1)) {
            parsedValue = parseInt(value, 10);
        } else if (typeof value === 'string' && booleanValueMap.hasOwnProperty(value)) {
            parsedValue = booleanValueMap[value];
        } else {
            parsedValue = value;
        }

        parsedValues[objectKey] = parsedValue;
    }

    return parsedValues;
}

function mergeValues(base: Record<string, any>, values: Record<string, any>) {
    for (const valuesKeys in values) {
        const value = values[valuesKeys];
        if (typeof value === 'object' && !Array.isArray(value)) {
            if (!base[valuesKeys]) {
                base[valuesKeys] = {};
            }
            mergeValues(base[valuesKeys], value);
        } else {
            set(base, valuesKeys, value);
        }
    }
}

export function useUrlSearchParams<T extends {}>(defaultValues: Partial<T> = {}): useUrlSearchParamsReturnValue<T> {
    const location = useLocation();
    const history = useHistory();

    const searchParams = useMemo(() => {
        const params = qs.parse(location.search.substring(1));

        let paramsDictionary: any = cloneDeep(defaultValues);

        if (params) {
            const parsedValues = parsePrimitiveValues(params);
            mergeValues(paramsDictionary, parsedValues);
        }

        return paramsDictionary;
    }, [location.search]);

    const setUrlSearchParams: any = (values: { [key: string]: any }) => {
        mergeValues(searchParams, values);
        history.replace(`?${qs.stringify(searchParams)}`);
    };

    return {
        urlSearchParams: searchParams as T,
        setUrlSearchParams
    };
}
